import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { Activity, CheckCircle2, AlertTriangle, Gauge, GitBranch, Cpu, HeartPulse } from 'lucide-react';
import {
  engineeringInsights,
  impactStrip,
  testTrendWeek,
  systemHealth,
} from '../data/mock';

const iconFor = (id) => {
  if (id === 'ci') return GitBranch;
  if (id === 'cov') return Gauge;
  if (id === 'fail') return AlertTriangle;
  return Activity;
};

const EngineeringInsights = () => {
  return (
    <section
      id="live-signal"
      className="py-16 md:py-20 px-6 border-b border-stone-400/25 dark:border-stone-500/20 scroll-mt-24"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6 md:mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-stone-900 dark:text-stone-100 tracking-tight">
              Live signal
            </h2>
            <p className="text-xs md:text-sm text-stone-600 dark:text-stone-400 mt-1 max-w-md leading-snug">
              Snapshot metrics—swap in real CI when wired.
            </p>
          </div>
          <div className="flex items-center gap-2 text-[10px] sm:text-xs text-stone-500 dark:text-stone-500 font-mono uppercase tracking-wider shrink-0">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
            Eng + quality
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 lg:items-start">
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-3">
              {engineeringInsights.map((item) => {
                const Icon = iconFor(item.id);
                const ok = item.status === 'ok';
                return (
                  <div
                    key={item.id}
                    className="rounded-xl border border-stone-200 dark:border-stone-700 bg-white/80 dark:bg-stone-950/60 p-3.5 md:p-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-400 leading-tight">
                        {item.label}
                      </span>
                      <Icon
                        className={`w-3.5 h-3.5 shrink-0 ${ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}
                      />
                    </div>
                    <p className="text-xl md:text-2xl font-bold text-stone-900 dark:text-stone-100 leading-none mb-1">
                      {item.value}
                    </p>
                    <p className="text-[11px] text-stone-600 dark:text-stone-400 leading-snug line-clamp-2">
                      {item.detail}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-5 lg:border-l lg:border-stone-200/80 lg:dark:border-stone-700 lg:pl-8 pt-2 lg:pt-0 border-t lg:border-t-0 border-stone-200/80 dark:border-stone-700">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 dark:text-stone-400 mb-3">
              Impact
            </p>
            <ul className="flex flex-col gap-2">
              {impactStrip.map((line) => (
                <li
                  key={line}
                  className="text-xs md:text-sm leading-snug px-3 py-2 rounded-lg bg-white/90 dark:bg-stone-950/60 border border-stone-200/90 dark:border-stone-700 text-stone-800 dark:text-stone-200 font-medium"
                >
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-10 border-t border-stone-200/80 dark:border-stone-700">
          <div className="mb-6 md:mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-stone-900 dark:text-stone-100 leading-tight">
              Live engineering dashboard
            </h3>
            <div className="w-12 h-0.5 bg-amber-800 dark:bg-amber-600 rounded-full mt-2 mb-2" />
            <p className="text-xs md:text-sm text-stone-600 dark:text-stone-400 max-w-2xl leading-snug">
              Weekly pass/fail trend and system health—illustrative; plug in your runner or observability stack.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-2xl border border-stone-200 dark:border-stone-700 bg-white/90 dark:bg-stone-950/50 p-4 md:p-6 shadow-sm">
              <h4 className="text-sm font-semibold text-stone-900 dark:text-stone-100 mb-4 flex items-center gap-2">
                <Cpu className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                Automated runs (passed vs failed)
              </h4>
              <div className="h-[260px] md:h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={testTrendWeek} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-stone-200 dark:stroke-stone-700" />
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} className="text-stone-500" />
                    <YAxis tick={{ fontSize: 12 }} className="text-stone-500" />
                    <Tooltip
                      contentStyle={{
                        borderRadius: '12px',
                        border: '1px solid rgb(231 229 228)',
                        background: 'rgba(255,255,255,0.95)',
                      }}
                    />
                    <Legend />
                    <Bar dataKey="passed" name="Passed" fill="rgb(180 83 9)" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="failed" name="Failed" stroke="rgb(220 38 38)" strokeWidth={2} dot />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-2xl border border-stone-200 dark:border-stone-700 bg-white/90 dark:bg-stone-950/50 p-5 md:p-6 shadow-sm flex flex-col">
              <h4 className="text-sm font-semibold text-stone-900 dark:text-stone-100 mb-4 flex items-center gap-2">
                <HeartPulse className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                System health
              </h4>
              <div className="flex-1 flex flex-col justify-center space-y-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-stone-500 dark:text-stone-400">Last build</p>
                  <p className="font-mono text-sm text-stone-900 dark:text-stone-100 mt-1">{systemHealth.build}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-stone-500 dark:text-stone-400">Deployed</p>
                  <p className="text-sm text-stone-800 dark:text-stone-200 mt-1">{systemHealth.deployedAt}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-stone-500 dark:text-stone-400">Environment</p>
                  <p className="text-sm text-stone-800 dark:text-stone-200 mt-1 capitalize">{systemHealth.environment}</p>
                </div>
                <div className="pt-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-900 dark:text-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {systemHealth.status}
                  </span>
                </div>
                <div className="pt-4 border-t border-stone-200 dark:border-stone-700">
                  <p className="text-xs uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-2">
                    Active automation flows
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {systemHealth.automationFlows.map((f) => (
                      <span
                        key={f}
                        className="text-xs px-2 py-1 rounded-md bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EngineeringInsights;
